export interface Phase { id: string; name: string; outcome: string }
export interface Section { id: string; phaseId: string; name: string; description: string }
export interface Sprint { id: string; phaseId: string; startDate: string; endDate: string; goal: string }
export interface WorkItem {
  id: string; title: string; phaseId: string; sectionId: string; sprintId: string;
  description: string; effortPoints: number; acceptanceCriteria: string[];
  completionRule: 'github_merge' | 'recorded_acceptance'; acceptanceOwner?: string;
  owner?: string; dependencies: string[]; requiredPrs: string[]; dependencyNotes?: string; supportingIssueLinks?: string[];
}
export interface Milestone { id: string; name: string; workIds: string[]; targetDate?: string }
export interface ScopeDocument {
  templateId: 'CW-SCOPE-1.0'; projectReference: string; scopeReference: string; revision: string;
  projectName: string; clientName: string; phases: Phase[]; sections: Section[];
  sprints: Sprint[]; workItems: WorkItem[]; milestones: Milestone[];
  sourceText?: string;
  metadata?: Record<string, string>;
}
export interface ValidationIssue { path: string; message: string }
export interface ValidationResult { valid: boolean; errors: ValidationIssue[]; scope?: ScopeDocument }
export interface PullRequestEvent {
  deliveryId: string; prUrl: string; state: 'open' | 'closed' | 'merged';
  baseBranch: string; targetBranch: string; updatedAt: string;
}
export interface PullRequestState extends PullRequestEvent {}
export interface ProjectState {
  scope: ScopeDocument; baselineScope: ScopeDocument; lastImportedScope: ScopeDocument;
  additionalWorkIds: string[]; prs: Record<string, PullRequestState>;
  acceptedWorkIds: string[]; processedEventIds: string[];
}
export interface Rollup { completedPoints: number; totalPoints: number; percent: number; completedItems: number; totalItems: number }
export interface Progress {
  project: Rollup; phases: Record<string, Rollup>; sections: Record<string, Rollup>;
  sprints: Record<string, Rollup>; workItems: Record<string, boolean>;
  milestones: Record<string, Rollup & { complete: boolean }>;
}
export interface ScopeChange {
  id: string; kind: 'created' | 'scope' | 'work' | 'progress'; revision: string; previousRevision?: string;
  createdAt: string;
  /** A client-visible explanation. Internal notes do not belong in history. */
  reason: string;
  /** Stored for administrators only; removed from client and contractor responses. */
  actorId?: string;
  added: string[]; removed: string[]; updated: string[]; reestimated: string[];
  completed: string[]; reopened: string[];
  before: Progress | null; after: Progress;
}
export type Role = 'codewizardz_admin' | 'contractor' | 'client';
export interface Principal { userId: string; role: Role; active: boolean; contractorId?: string; projectIds?: string[] }
export interface ProjectAccess { id: string; engagement: 'direct' | 'contractor'; contractorId?: string }
export type NoteAudience = 'internal' | 'contractor' | 'client';
export interface ProjectNote {
  id: string; projectId: string; body: string; audience: NoteAudience;
  source: 'github' | 'contractor' | 'codewizardz'; authorId: string; createdAt: string;
  workId?: string; sectionId?: string; sourceUrl?: string;
}
export interface ReportConfig {
  id: string; audience: NoteAudience; cadence: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  deliveryMode: 'automatic' | 'review'; anchorDate?: string;
  timezone: string; localTime: string; dayOfWeek?: number; dayOfMonth?: number;
  recipientUserIds: string[]; sections: Array<'summary' | 'progress' | 'milestones' | 'notes' | 'scope_changes'>;
  includeUnchanged: boolean; enabled: boolean;
}
export class DomainError extends Error {
  constructor(message: string, public readonly issues: ValidationIssue[] = []) { super(message); this.name = 'DomainError'; }
}
